import { FC, JSX, useContext, useReducer, useRef, useState } from "react";
import { Popup } from "react-map-gl/mapbox";
import {
  NetworkAssessmentAnalysisType,
  ProjectionProviderType,
  ProviderActionStatusEnum,
  ProviderBadgeStyle
} from "@app/projects/enumerators";
import { ProviderMapResults } from "@app/projects/results/common/mapping";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { formatPercent } from "@app/projects/zip-projects/maps/providerMetricFormat";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import { buildServicingProviderFromProviderMapResults } from "@app/projects/servicing-provider/utility";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import DeleteProjectionProvider, {
  WrappedDeleteProjectionProvider
} from "@app/projects/projection-provider/DeleteProjectionProvider";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState,
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { convertServicingProvidersToAccessPoints } from "@app/projects/projection-provider/utils";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectOptions from "@app/projects/projectOptions";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

export interface ZipCoverageMapProviderPopupProps {
  provider: ProviderMapResults;
  onClose: () => void;
  showProviderActionStatus: boolean;
  runConfiguration: ProjectRunConfigurationResult;
  specialtyGroupCode: string;
  regionSetId: number | null;
  project: ProjectOptions;
  canTargetProviders: boolean;
  zipBaselineRow: ZipCodeDisplayModel | null;
  zipClassBaselineRow: ZipClassAggregateResultDisplayModel | null;
  onTargetSubmitted: (runProjection: boolean, hasUnprocessedProviders?: boolean) => void;
  onTargetRemoved: (runProjection: boolean) => void;
}

export const ZipCoverageMapProviderPopup: FC<ZipCoverageMapProviderPopupProps> = ({
  provider,
  onClose,
  showProviderActionStatus,
  runConfiguration,
  specialtyGroupCode,
  regionSetId,
  project,
  canTargetProviders,
  zipBaselineRow,
  zipClassBaselineRow,
  onTargetSubmitted,
  onTargetRemoved,
}) => {
  const userContext = useContext(UserContext);
  const [projectionProvidersState, projectionProvidersDispatch] =
    useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
  const [isRemoveTargetModalOpen, setIsRemoveTargetModalOpen] = useState(false);
  const deleteProjectionProviderRef = useRef<WrappedDeleteProjectionProvider>(null);

  const isNetworkProvider = provider.providerNetworkType === ProviderNetworkTypeEnum.Network;
  const isTerminatedProvider = provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated;
  const isTargetProvider = provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted;
  const isExcludedProvider = provider.scenarioModelProviderStatus  === ProviderActionStatusEnum.FullyExcluded;
  const isProjected = isTargetProvider || isExcludedProvider;

  // isRunning is redundant with isReadOnly today; stated so the "greyed out while processing" rule is explicit.
  const isActionDisabled = project.isReadOnly || project.isEditRestricted || project.isRunning;
  const showTarget = canTargetProviders && showProviderActionStatus && !!regionSetId
    && !isNetworkProvider && !isTargetProvider && !isExcludedProvider;
  const showRemoveTarget = canTargetProviders && showProviderActionStatus && isTargetProvider
    && !!provider.scenarioModelProviderProjectId;

  const getProviderFlyout = (): JSX.Element | undefined => {
    if(regionSetId) {
      return (
        <ServicingProviderProfile
          isTerminated={isTerminatedProvider}
          isProjected={isProjected}
          isZipProject={true}
          provider={buildServicingProviderFromProviderMapResults(provider)}
          project={project}
          runConfiguration={runConfiguration}
          regionSetId={regionSetId}
          specialtyGroupCode={specialtyGroupCode}
          providerNetworkType={provider.providerNetworkType} />
      )
    }
  };

  const getCreateProjectionProvider = (): JSX.Element => {
    const selectedProviders = [buildServicingProviderFromProviderMapResults(provider)];

    return (
      <ProjectionProviderDetails
        accessPoints={convertServicingProvidersToAccessPoints(selectedProviders, provider.providerNetworkType, specialtyGroupCode)}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersDispatch}
        hasProvidersWithoutNPI={!provider.npi}
        isRunning={project.isRunning}
        isZipProject={true}
        networkAssessmentAnalysisType={NetworkAssessmentAnalysisType.ServiceArea}
        onProjectionProvidersCreated={onTargetSubmitted}
        projectId={project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={provider.providerNetworkType}
        showAffiliationsInsights={project.showAffiliationsInsights}
        specialtyGroupCode={specialtyGroupCode}
      />
    );
  };

  const getPreviewTargetingContent = (): JSX.Element => {
    const showAvgDist = runConfiguration.showDistance && project.showAverageDistance;
    const showAvgTime = runConfiguration.showTime && project.showAverageTime;
    const showWithTimeAndDistanceColumns = runConfiguration.showWithTimeAndDistanceColumns;
    const showNetNew = userContext.roles.includes(Roles.VOLUME_TARGET);

    return (
      <SimulateProviderTargeting
        formState={projectionProvidersState}
        formDispatch={projectionProvidersDispatch}
        isZipProject={true}
        processingContent={getCreateProjectionProvider()}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={provider.providerNetworkType}
        regionSetId={regionSetId!}
        runConfigurationOverride={runConfiguration}
        selectedProviders={[buildServicingProviderFromProviderMapResults(provider)]}
        specialtyGroupCode={specialtyGroupCode}
        user={userContext}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns }}
        zipBaselineDetails={zipBaselineRow ? [zipBaselineRow] : []}
        zipClassBaselineDetails={zipClassBaselineRow ? [zipClassBaselineRow] : []}
      />
    );
  };

  const handleProjectionProvidersDeleted = (runProjection: boolean) => {
    onTargetRemoved(runProjection);
    setIsRemoveTargetModalOpen(false);
  };

  const handleConfirmRemoveTarget = async () => {
    await deleteProjectionProviderRef.current?.handleSubmit();
  };

  const getDeleteProjectionProvider = (): JSX.Element => (
    <DeleteProjectionProvider
      netNewServicingProviderProjectIds={[]}
      onProjectionProvidersDeleted={handleProjectionProvidersDeleted}
      projectId={project.projectId}
      projectionProviderProjectIds={[provider.scenarioModelProviderProjectId]}
      projectionProviderType={ProjectionProviderType.Target}
      providerNetworkType={provider.providerNetworkType}
      ref={deleteProjectionProviderRef}
      wrapperComponentType="modal"
    />
  );

  return (
    <Popup
      anchor="bottom"
      className="zip-coverage-map__provider-popup"
      closeButton={true}
      closeOnClick={false}
      latitude={provider.latitude}
      longitude={provider.longitude}
      offset={[0, -20]}
      onClose={onClose}
    >
      <div className="provider-popup__title">{provider.displayName}</div>
      <div className="provider-popup__separator" />
      <div className="provider-popup__badge">
        <ProviderNetworkTypeBadge
          badgeStyle={ProviderBadgeStyle.Outline}
          providerNetworkType={provider.providerNetworkType}
        />
        {showProviderActionStatus && (
          <ProviderActionStatusBadge
            badgeStyle={ProviderBadgeStyle.Outline}
            hideWhenNullActionStatus={true}
            status={provider.scenarioModelProviderStatus}
          />
        )}
      </div>

      <div className="provider-popup__row">
        <strong>NPI:</strong> {provider.npi}
      </div>

      <div className="provider-popup__row">
        <strong>Address:</strong> {provider.address}, {provider.city}, {provider.state} {provider.zip}
      </div>

      <div className="provider-popup__row provider-popup__row--metrics">
        <span><strong>Serves:</strong> {formatPercent(provider.servesPercent)}</span>
        {!isNetworkProvider &&
          <>
            <span><strong>Gains:</strong> {formatPercent(provider.gainsPercent)}</span>
            <span><strong>Impact:</strong> {formatPercent(provider.impactsPercent)}</span>
          </>
        }
      </div>

      <div className="provider-popup__row">
        <FlyoutLink
          classNames="provider-details-button"
          content={{
            body: getProviderFlyout(),
            title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
          }}
          disabled={!regionSetId}
          size="40"
          testId='zip-coverage-map__provider-popup-view-provider-details-button-flyout'>
          View Provider Details
        </FlyoutLink>
      </div>

      {showTarget && (
        <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
          <div className="provider-popup__row">
            <FlyoutLink
              classNames="qes-button-primary"
              content={{
                body: getPreviewTargetingContent(),
                title: "Target Provider",
              }}
              disabled={isActionDisabled}
              testId='zip-coverage-map__provider-popup-target-provider-button-flyout-with-preview'>
              Target Provider
            </FlyoutLink>
          </div>
        </RoleRestricted>
      )}

      {showRemoveTarget && (
        <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
          <div className="provider-popup__row">
            <QESButton
              disabled={isActionDisabled}
              onClick={() => setIsRemoveTargetModalOpen(true)}
              qesTestId="zip-coverage-map__provider-popup-remove-provider-from-targeting-modal"
              qesType="primary">
              Remove Target
            </QESButton>
            <Modal
              closeModal={() => setIsRemoveTargetModalOpen(false)}
              isVisible={isRemoveTargetModalOpen}
              content={{
                body: getDeleteProjectionProvider(),
                cancelButtonText: "Cancel",
                confirmButtonAction: handleConfirmRemoveTarget,
                confirmButtonText: "Submit",
                confirmButtonType: "primary",
                title: "Delete Targeted Provider",
              }}
            />
          </div>
        </RoleRestricted>
      )}
    </Popup>
  );
};

export default ZipCoverageMapProviderPopup;
